import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Floating Event Widget — sticky bottom-right pill linking to the Invest UAE Forum.
 * Auto-hides on the forum page itself, on masterclass, and any /admin routes.
 * Auto-disappears after 31 May 2026 (forum date).
 */
const FORUM_DATE = new Date('2026-06-01T00:00:00Z'); // hide widget after this
const STORAGE_KEY = 'ski-axis-event-widget-dismissed';

const STYLES = `
.ski-event-widget{position:fixed;bottom:24px;left:24px;z-index:280;display:flex;align-items:center;gap:0;background:linear-gradient(135deg,#0D1B2A 0%,#1A2F4A 100%);color:#FAF7F0;border:1px solid rgba(200,168,75,.4);border-radius:50px;box-shadow:0 8px 32px rgba(13,27,42,.35),0 0 0 1px rgba(200,168,75,.08);padding:8px 8px 8px 22px;font-family:'DM Sans',-apple-system,sans-serif;animation:swEnter .6s cubic-bezier(.16,1,.3,1) both;cursor:pointer;transition:transform .25s,box-shadow .25s;}
.ski-event-widget:hover{transform:translateY(-2px);box-shadow:0 12px 40px rgba(13,27,42,.45),0 0 0 1px rgba(200,168,75,.2);}
.ski-event-widget-pulse{width:8px;height:8px;border-radius:50%;background:#C8A84B;margin-right:10px;animation:swPulse 2s infinite;}
.ski-event-widget-text{display:flex;flex-direction:column;line-height:1.2;text-align:left;margin-right:14px;}
.ski-event-widget-eyebrow{font-size:8px;font-weight:700;letter-spacing:.22em;text-transform:uppercase;color:#C8A84B;margin-bottom:3px;}
.ski-event-widget-title{font-size:12px;font-weight:600;color:#FAF7F0;letter-spacing:.01em;}
.ski-event-widget-cta{background:#C8A84B;color:#0D1B2A;font-size:9px;font-weight:800;letter-spacing:.18em;text-transform:uppercase;padding:9px 16px;border-radius:50px;border:none;cursor:pointer;transition:background .2s;}
.ski-event-widget-cta:hover{background:#FAF7F0;}
.ski-event-widget-x{background:none;border:none;color:rgba(250,247,240,.4);font-size:18px;line-height:1;cursor:pointer;width:22px;height:22px;display:flex;align-items:center;justify-content:center;margin-left:6px;border-radius:50%;transition:color .2s,background .2s;}
.ski-event-widget-x:hover{color:#FAF7F0;background:rgba(255,255,255,.08);}
@keyframes swEnter{from{opacity:0;transform:translateY(20px);}to{opacity:1;transform:none;}}
@keyframes swPulse{0%,100%{opacity:1;transform:scale(1);box-shadow:0 0 0 0 rgba(200,168,75,.4);}50%{opacity:.6;transform:scale(1.15);box-shadow:0 0 0 6px rgba(200,168,75,0);}}
@media(max-width:640px){
  .ski-event-widget{bottom:16px;left:16px;right:16px;padding:6px 6px 6px 14px;}
  .ski-event-widget-text{margin-right:8px;}
  .ski-event-widget-title{font-size:11px;}
  .ski-event-widget-cta{font-size:8px;padding:7px 12px;}
}
`;

export default function FloatingEventWidget() {
  const location = useLocation();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setDismissed(sessionStorage.getItem(STORAGE_KEY) === '1');
  }, []);

  // Hide if past forum date
  if (new Date() > FORUM_DATE) return null;
  if (dismissed) return null;

  // Hide on the forum page, the speaker profiles, masterclass, admin, and auth pages
  const path = location.pathname;
  if (path.startsWith('/events/')) return null;
  if (path.startsWith('/forum/')) return null;
  if (path === '/masterclass') return null;
  if (path.startsWith('/admin')) return null;
  if (path === '/auth' || path === '/login' || path === '/signup' || path.startsWith('/auth/')) return null;
  if (path === '/payment-success') return null;
  if (path === '/kyc' || path === '/security') return null;

  const dismiss = (e) => {
    e.stopPropagation();
    sessionStorage.setItem(STORAGE_KEY, '1');
    setDismissed(true);
  };

  const go = () => window.open('https://us06web.zoom.us/j/84373516075?pwd=9fhqBBuUJFwYQDcv0gM2drjBlV6az8.1', '_blank', 'noreferrer');

  return (
    <>
      <style>{STYLES}</style>
      <div
        className="ski-event-widget"
        onClick={go}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go(); } }}
        data-testid="floating-event-widget"
      >
        <span className="ski-event-widget-pulse" aria-hidden="true" />
        <div className="ski-event-widget-text">
          <span className="ski-event-widget-eyebrow">Live · 31 May 2026</span>
          <span className="ski-event-widget-title">Invest UAE Forum</span>
        </div>
        <button className="ski-event-widget-cta" onClick={(e) => { e.stopPropagation(); go(); }} data-testid="floating-event-cta">
          Join Zoom · 31 May
        </button>
        <button className="ski-event-widget-x" onClick={dismiss} aria-label="Dismiss" data-testid="floating-event-dismiss">×</button>
      </div>
    </>
  );
}
